import { ArrowRightIcon, CheckCircle2Icon, ChevronRightIcon, DotIcon, LayoutGridIcon, TargetIcon, XIcon } from "lucide-react";
import { Link } from "react-router-dom";
import EmbeddedTweet from "@/components/EmbeddedTweet";
import UserAvatar from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";

// ============================================================================
// TYPOGRAPHY SYSTEM
// ============================================================================

const bodyFont = "font-sans";
const monoFont = "font-mono";

// ============================================================================
// DATA & CONTENT
// ============================================================================

const heroStats = [
  { label: "Memos Tracked", value: "1,247", change: "+12%" },
  { label: "Decisions Made", value: "384", change: "+8%" },
  { label: "Active Tickers", value: "56", change: "+3" },
  { label: "Avg Improvement", value: "23%", change: "QoQ" },
];

const problemData = [
  {
    pattern: "情绪化交易",
    frequency: "73%",
    impact: "high",
    description: "缺乏明确纪律约束，导致冲动买卖",
  },
  {
    pattern: "逻辑断层",
    frequency: "68%",
    impact: "critical",
    description: "买卖决策与原始研究结论脱节",
  },
  {
    pattern: "复盘困难",
    frequency: "54%",
    impact: "medium",
    description: "历史决策缺乏上下文，难以评估",
  },
];

const solutionMatrix = [
  {
    category: "Capture",
    tool: "Memo Node",
    capability: "快速记录市场观察",
    metric: "2.3s",
    label: "平均记录时间",
  },
  {
    category: "Structure",
    tool: "Decision Node",
    capability: "结构化买卖决策",
    metric: "100%",
    label: "逻辑完整性",
  },
  {
    category: "Guardrail",
    tool: "Self-Contract",
    capability: "强制交易纪律执行",
    metric: "-67%",
    label: "情绪化交易减少",
  },
  {
    category: "Review",
    tool: "Ticker Timeline",
    capability: "完整决策历史回溯",
    metric: "∞",
    label: "时间跨度",
  },
];

const workflowSteps = [
  {
    step: "01",
    title: "Capture",
    duration: "<30s",
    description: "记录市场观察，标记 Ticker 和标签",
    status: "active",
  },
  {
    step: "02",
    title: "Structure",
    duration: "~2min",
    description: "将想法转化为结构化决策节点",
    status: "pending",
  },
  {
    step: "03",
    title: "Guardrail",
    duration: "Ongoing",
    description: "设置 Self-Contract 约束交易行为",
    status: "pending",
  },
  {
    step: "04",
    title: "Review",
    duration: "Weekly",
    description: "按 Ticker 和时间轴复盘决策质量",
    status: "pending",
  },
];

const capabilities = [
  {
    title: "Memo Node",
    type: "自由记录",
    description: "Markdown + $Ticker + #Tag，快速捕捉想法",
    features: ["实时渲染", "AI 辅助分析", "自动归档"],
  },
  {
    title: "Decision Node",
    type: "结构化决策",
    description: "入场、加减仓、退出的完整逻辑链",
    features: ["Thesis 字段", "风险评估", "信心度打分"],
  },
  {
    title: "Self-Contract",
    type: "行为约束",
    description: "仓位上限、止损纪律、冷静期规则",
    features: ["强制检查", "违规提醒", "合规统计"],
  },
  {
    title: "Checklist",
    type: "执行清单",
    description: "开仓前必检项，降低操作失误",
    features: ["自定义模板", "快速勾选", "团队共享"],
  },
];

const comparativeData = [
  {
    dimension: "记录方式",
    without: "散落在聊天记录、截图、笔记本",
    with: "统一平台，结构化存储，智能检索",
  },
  {
    dimension: "执行约束",
    without: "纪律依赖意志力，容易失控",
    with: "Self-Contract 强制检查，系统化约束",
  },
  {
    dimension: "复盘效率",
    without: "回忆困难，缺少上下文",
    with: "时间轴 + Ticker 聚合，一键还原",
  },
  {
    dimension: "策略迭代",
    without: "凭感觉调整，缺少数据支持",
    with: "历史决策数据化，量化改进效果",
  },
];

// ============================================================================
// UI COMPONENTS
// ============================================================================

const StatusBadge = ({ status, children }: { status: "active" | "pending" | "success" | "warning"; children: React.ReactNode }) => {
  const styles = {
    active: "bg-primary/10 text-primary border-primary/30",
    pending: "bg-muted text-muted-foreground border-border",
    success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    warning: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-md border ${styles[status]}`}>
      {children}
    </span>
  );
};

const StatCard = ({ label, value, change }: { label: string; value: string; change: string }) => (
  <div className="flex items-baseline justify-between border-b border-border py-3">
    <span className="text-sm text-muted-foreground">{label}</span>
    <div className="flex items-baseline gap-3">
      <span className={`text-2xl font-semibold tabular-nums ${monoFont}`}>{value}</span>
      <span className="text-xs text-emerald-600 dark:text-emerald-400 tabular-nums">{change}</span>
    </div>
  </div>
);

const ProblemRow = ({ item, index }: { item: (typeof problemData)[0]; index: number }) => {
  const impactColors = {
    high: "text-amber-600 dark:text-amber-400",
    critical: "text-red-600 dark:text-red-400",
    medium: "text-yellow-600 dark:text-yellow-400",
  };

  return (
    <div className="grid grid-cols-12 gap-4 py-3 border-b border-border/50 items-center">
      <span className={`text-xs ${monoFont} tabular-nums text-muted-foreground`}>0{index + 1}</span>
      <div className="col-span-4">
        <span className="text-sm font-medium">{item.pattern}</span>
      </div>
      <div className="col-span-3">
        <span className={`text-sm font-semibold ${monoFont} tabular-nums`}>{item.frequency}</span>
      </div>
      <div className="col-span-2">
        <span className={`text-xs font-medium uppercase ${impactColors[item.impact as keyof typeof impactColors]}`}>{item.impact}</span>
      </div>
      <div className="col-span-2 text-right">
        <span className="text-xs text-muted-foreground">{item.description}</span>
      </div>
    </div>
  );
};

const SolutionCard = ({ item }: { item: (typeof solutionMatrix)[0] }) => (
  <div className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
    <div className="flex items-start justify-between mb-3">
      <div>
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{item.category}</span>
        <h3 className="text-lg font-semibold mt-1">{item.tool}</h3>
      </div>
      <span className={`text-xl font-bold ${monoFont} tabular-nums text-primary`}>{item.metric}</span>
    </div>
    <p className="text-sm text-muted-foreground mb-2">{item.capability}</p>
    <span className="text-xs text-muted-foreground">{item.label}</span>
  </div>
);

const WorkflowStep = ({ step }: { step: (typeof workflowSteps)[0] }) => (
  <div className="flex gap-4 py-4 border-b border-border/50 last:border-0">
    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
      <span className={`text-sm font-semibold ${monoFont}`}>{step.step}</span>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h3 className="text-sm font-semibold">{step.title}</h3>
        <span className={`text-xs ${monoFont} tabular-nums text-muted-foreground flex-shrink-0`}>{step.duration}</span>
      </div>
      <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
      {step.status === "active" && (
        <StatusBadge status="active">
          <DotIcon className="w-3 h-3 fill-current animate-pulse" />
          Active
        </StatusBadge>
      )}
    </div>
  </div>
);

const CapabilityCard = ({ item }: { item: (typeof capabilities)[0] }) => (
  <div className="border border-border rounded-lg p-5 hover:border-primary/30 transition-colors">
    <div className="flex items-start justify-between mb-3">
      <div>
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{item.type}</span>
        <h3 className="text-lg font-semibold mt-1">{item.title}</h3>
      </div>
      <div className="w-8 h-8 rounded bg-muted/50 flex items-center justify-center">
        <LayoutGridIcon className="w-4 h-4 text-muted-foreground" />
      </div>
    </div>
    <p className="text-sm text-muted-foreground mb-4">{item.description}</p>
    <div className="flex flex-wrap gap-2">
      {item.features.map((feature) => (
        <span key={feature} className="text-xs px-2 py-1 rounded bg-muted/50 text-muted-foreground">
          {feature}
        </span>
      ))}
    </div>
  </div>
);

// ============================================================================
// MAIN LANDING PAGE
// ============================================================================

const LandingV1_2 = () => {
  return (
    <div className={`min-h-screen bg-background text-foreground ${bodyFont}`}>
      {/* Grid Background Pattern */}
      <div
        className="fixed inset-0 pointer-events-none -z-10 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserAvatar avatarUrl="/full-logo.webp" className="w-5 h-5 rounded" />
            <span className="font-semibold text-sm">HamEcho</span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#problems" className="hover:text-foreground transition-colors">
              问题分析
            </a>
            <a href="#solution" className="hover:text-foreground transition-colors">
              解决方案
            </a>
            <a href="#capabilities" className="hover:text-foreground transition-colors">
              核心功能
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/auth" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Log in
            </Link>
            <Button size="sm" className="h-8 px-4 text-sm rounded-md" asChild>
              <Link to="/auth/signup">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="border-b border-border">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              {/* Left: Content */}
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-medium">
                  <DotIcon className="w-3 h-3 fill-current" />
                  Decision System for Investors
                </div>

                <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">
                  投资不缺观点，
                  <br />
                  缺的是可验证的系统。
                </h1>

                <p className="text-lg text-muted-foreground leading-relaxed">
                  HamJournal 将想法记录、执行约束、结果复盘形成完整回路。让每一次交易决策都有据可查、有规则可循、有数据可改进。
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button size="lg" className="h-11 px-6 rounded-md text-base" asChild>
                    <Link to="/auth/signup">
                      开始构建交易系统
                      <ArrowRightIcon className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" className="h-11 px-6 rounded-md text-base" asChild>
                    <Link to="/auth">查看 Demo</Link>
                  </Button>
                </div>
              </div>

              {/* Right: Stats Panel */}
              <div className="border border-border rounded-lg bg-muted/20 p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium">平台数据统计</span>
                  <span className="text-xs text-muted-foreground">Live</span>
                </div>

                <div className="space-y-1">
                  {heroStats.map((stat) => (
                    <StatCard key={stat.label} {...stat} />
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    <CheckCircle2Icon className="w-3 h-3 inline mr-1 text-emerald-500" />
                    数据实时更新 · 基于 1,247 位活跃用户
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problems Section */}
        <section id="problems" className="border-b border-border bg-muted/10">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
            <div className="mb-10">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Problem Analysis</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-3">投资者行为模式分析</h2>
              <p className="text-muted-foreground mt-3 max-w-2xl">基于用户访谈和行为数据，我们识别出三个核心痛点：</p>
            </div>

            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 py-2 border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
              <span className="col-span-1">#</span>
              <span className="col-span-4">行为模式</span>
              <span className="col-span-3">发生频率</span>
              <span className="col-span-2">影响</span>
              <span className="col-span-2 text-right">说明</span>
            </div>

            {/* Problem Rows */}
            <div className="mt-2">
              {problemData.map((item, index) => (
                <ProblemRow key={item.pattern} item={item} index={index} />
              ))}
            </div>

            {/* Summary Box */}
            <div className="mt-8 p-5 border border-border rounded-lg bg-background">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded bg-amber-500/10 flex items-center justify-center">
                  <TargetIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">核心洞察</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    投资亏损的主要原因不是认知不足，而是行为失控。HamJournal 通过结构化记录和强制约束，帮助投资者建立可持续的决策系统。
                  </p>
                </div>
              </div>
            </div>

            {/* Embedded Tweet - Key Insight */}
            <div className="mt-8" style={{ minHeight: "400px" }}>
              <EmbeddedTweet />
            </div>
          </div>
        </section>

        {/* Solution Matrix */}
        <section id="solution" className="border-b border-border">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
            <div className="mb-10">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Solution Framework</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-3">四步形成决策回路</h2>
              <p className="text-muted-foreground mt-3 max-w-2xl">从想法记录到行为约束，形成完整闭环：</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {solutionMatrix.map((item) => (
                <SolutionCard key={item.category} item={item} />
              ))}
            </div>

            {/* Workflow Visualization */}
            <div className="mt-12 border border-border rounded-lg bg-muted/10 p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-6">执行流程</h3>
              <div className="max-w-2xl">
                {workflowSteps.map((step) => (
                  <WorkflowStep key={step.step} step={step} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Grid */}
        <section id="capabilities" className="border-b border-border bg-muted/10">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
            <div className="mb-10">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Core Capabilities</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-3">四种基石 Node</h2>
              <p className="text-muted-foreground mt-3 max-w-2xl">每种节点服务于决策链中的特定环节：</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {capabilities.map((item) => (
                <CapabilityCard key={item.title} item={item} />
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="border-b border-border">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
            <div className="mb-10">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Comparative Analysis</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-3">使用前后对比</h2>
            </div>

            <div className="border border-border rounded-lg overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 p-4 bg-muted/50 border-b border-border text-sm font-semibold">
                <span className="col-span-3">维度</span>
                <span className="col-span-4">Without HamJournal</span>
                <span className="col-span-4">With HamJournal</span>
                <span className="col-span-1" />
              </div>

              {/* Table Rows */}
              {comparativeData.map((item) => (
                <div key={item.dimension} className="grid grid-cols-12 gap-4 p-4 border-b border-border last:border-0 items-start">
                  <span className="col-span-3 text-sm font-medium">{item.dimension}</span>
                  <div className="col-span-4 flex items-start gap-2">
                    <XIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{item.without}</span>
                  </div>
                  <div className="col-span-4 flex items-start gap-2">
                    <CheckCircle2Icon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{item.with}</span>
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="border-b border-border bg-muted/10">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">市场不可控。行为可以被约束。</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              从今天开始，把交易纪律写进系统。让每次操作都有依据、有约束、可复盘。
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" className="h-12 px-8 rounded-md text-base" asChild>
                <Link to="/auth/signup">
                  创建免费账号
                  <ArrowRightIcon className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-8 rounded-md text-base" asChild>
                <Link to="/auth">了解详情</Link>
              </Button>
            </div>

            <div className="mt-12 pt-8 border-t border-border">
              <div className="flex flex-wrap justify-center gap-8 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="w-4 h-4 text-emerald-500" />
                  <span>免费开始</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="w-4 h-4 text-emerald-500" />
                  <span>无信用卡要求</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="w-4 h-4 text-emerald-500" />
                  <span>数据安全保护</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/20">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <UserAvatar avatarUrl="/full-logo.webp" className="w-5 h-5 rounded" />
                <span className="font-semibold text-sm">HamEcho</span>
              </div>
              <p className="text-sm text-muted-foreground max-w-sm">
                AI-native investment research platform. Build your decision system with structured journals and disciplined trading.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/" className="hover:text-foreground transition-colors">
                    HamJournal
                  </Link>
                </li>
                <li>
                  <a href="#capabilities" className="hover:text-foreground transition-colors">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-foreground transition-colors">
                    Pricing
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-3">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <a href="#" className="hover:text-foreground transition-colors">
                    About
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-foreground transition-colors">
                    Privacy
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-foreground transition-colors">
                    Terms
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <p>© 2026 HamEcho. All rights reserved.</p>
            <div className="flex items-center gap-2">
              <DotIcon className="w-3 h-3 fill-current text-emerald-500" />
              <span className="text-xs">System Operational</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingV1_2;
